import useTaxCustomer from '../taxcontext/useTaxCustomer'
// src/features/taxpayers/taxpayersApiSlice.js
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import { apiSlice } from '../../app/api/apiSlice'

const taxpayersAdapter = createEntityAdapter({
  selectId: (taxpayer) =>
    String(
      taxpayer?.taxpayer_id ??
      taxpayer?.id ??
      taxpayer?._id ??
      ''
    ),
})

const initialState = taxpayersAdapter.getInitialState({ total: 0 })

export const taxpayersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTaxpayers: builder.query({
      query: (params = {}) => ({
        url: '/taxpayers',
        params,
      }),

      transformResponse: (response, meta) => {
        let items = []
        let total = 0

        if (
          response &&
          typeof response === 'object' &&
          'items' in response &&
          'total' in response
        ) {
          items = response.items ?? []
          total = Number(response.total ?? 0)
        } else if (Array.isArray(response)) {
          items = response
          const headerTotal =
            meta?.response?.headers?.get('X-Total-Count') ??
            meta?.response?.headers?.get('x-total-count')

          total = headerTotal ? Number(headerTotal) : items.length
        }

        const processed = items.map((taxpayer) => {
          const sid = String(
            taxpayer?.taxpayer_id ??
            taxpayer?.id ??
            taxpayer?._id ??
            ''
          )

          return {
            ...taxpayer,
            id: sid,
            _id: sid,
          }
        })

        return taxpayersAdapter.setAll(
          { ...initialState, total },
          processed
        )
      },

      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'Taxpayer', id })),
              { type: 'Taxpayer', id: 'LIST' },
            ]
          : [{ type: 'Taxpayer', id: 'LIST' }],
    }),

    getTaxpayerById: builder.query({
      query: ({ id, customer_id }) => ({ url: `/taxpayers/${id}`, params: { customer_id } }),
      providesTags: (_res, _err, id) => [{ type: 'Taxpayer', id: String(id.id) }],
    }),

    addNewTaxpayer: builder.mutation({
      query: (body) => ({
        url: '/taxpayers',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Taxpayer', id: 'LIST' }],
    }),

    updateTaxpayer: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/taxpayers/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Taxpayer', id: String(id) },
        { type: 'Taxpayer', id: 'LIST' },
      ],
    }),

    deleteTaxpayer: builder.mutation({
      query: ({ id, customer_id }) => ({
        url: `/taxpayers/${id}`,
        method: 'DELETE',
        params: { customer_id },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Taxpayer', id: String(id) },
        { type: 'Taxpayer', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useGetTaxpayersQuery,
  useGetTaxpayerByIdQuery: useScopedTaxpayerByIdQuery,
  useAddNewTaxpayerMutation: useScopedAddTaxpayerMutation,
  useUpdateTaxpayerMutation: useScopedUpdateTaxpayerMutation,
  useDeleteTaxpayerMutation: useScopedDeleteTaxpayerMutation,
} = taxpayersApiSlice

const selectGetTaxpayersResult =
  taxpayersApiSlice.endpoints.getTaxpayers.select

export const makeSelectTaxpayersData = (args) =>
  createSelector(
    selectGetTaxpayersResult(args),
    (res) => res?.data ?? initialState
  )

export const makeTaxpayerSelectors = (args) => {
  const selectData = makeSelectTaxpayersData(args)
  const baseSelectors = taxpayersAdapter.getSelectors((state) =>
    selectData(state)
  )

  return {
    selectAllTaxpayers: baseSelectors.selectAll,
    selectTaxpayerById: baseSelectors.selectById,
    selectTaxpayerIds: baseSelectors.selectIds,
    selectTaxpayerEntities: baseSelectors.selectEntities,
    selectTotal: (state) => selectData(state).total ?? 0,
  }
}
// Keep existing form calls compatible while putting customer scope in every request.
export function useGetTaxpayerByIdQuery(id, options = {}) {
  const customer_id = useTaxCustomer();
  return useScopedTaxpayerByIdQuery({ id, customer_id }, { ...options, skip: options.skip || !customer_id || !id });
}
function useCustomerMutation(useMutation) {
  const customer_id = useTaxCustomer();
  const [trigger, result] = useMutation();
  return [(payload = {}) => trigger({ ...payload, customer_id }), result];
}
export function useAddNewTaxpayerMutation() { return useCustomerMutation(useScopedAddTaxpayerMutation); }
export function useUpdateTaxpayerMutation() { return useCustomerMutation(useScopedUpdateTaxpayerMutation); }
export function useDeleteTaxpayerMutation() { return useCustomerMutation(useScopedDeleteTaxpayerMutation); }
